"use client";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  usePlanUsageQuery,
  type PlanUsageSnapshot,
} from "@/hooks/queries/usePlanUsageQuery";
import { useProUpgradeLimitDialog } from "@/hooks/useProUpgradeLimitDialog";
import { collectAllScrollsFromQueryCache } from "@/lib/client/scroll-query-cache";
import {
  countLivePublishedScrolls,
  isPublishLimitReached,
  resolveWriteBlockKind,
} from "@/lib/plan-access-client";
import { FREE_TIER_MAX_PUBLISHED } from "@/lib/plan-limits";
import { queryKeys } from "@/lib/react-query/query-keys";
import type { Scroll } from "@/lib/supabase";

function readPlanUsageSnapshot(
  qc: ReturnType<typeof useQueryClient>,
  cached?: PlanUsageSnapshot,
): PlanUsageSnapshot | undefined {
  return (
    qc.getQueryData<PlanUsageSnapshot>(queryKeys.planUsage()) ?? cached
  );
}

type PublishGuardContext = {
  scroll?: Pick<Scroll, "plan_locked" | "publish_paused"> | null;
  locked?: boolean;
};

export function usePublishLimitGate() {
  const qc = useQueryClient();
  const { data: usage } = usePlanUsageQuery();
  const {
    showProLimit,
    showReadOnly,
    handlePlanAccessError,
    guardWriteAccess,
    proLimitDialog,
  } = useProUpgradeLimitDialog();

  const guardPublish = useCallback(
    (
      alreadyPublished: boolean,
      context?: PublishGuardContext,
    ): boolean => {
      if (context?.locked) {
        showReadOnly("note");
        return false;
      }

      const blocked = resolveWriteBlockKind({
        scroll: context?.scroll,
        action: "publish",
      });
      if (blocked) {
        guardWriteAccess({ scroll: context?.scroll, action: "publish" });
        return false;
      }

      if (alreadyPublished) return true;

      const snapshot = readPlanUsageSnapshot(qc, usage);
      if (snapshot) {
        if (snapshot.isPro) return true;
        if (isPublishLimitReached(snapshot, false)) {
          showProLimit("publish");
          return false;
        }
        return true;
      }

      const publishedCount = countLivePublishedScrolls(
        collectAllScrollsFromQueryCache(qc),
      );
      if (publishedCount >= FREE_TIER_MAX_PUBLISHED) {
        showProLimit("publish");
        return false;
      }

      return true;
    },
    [guardWriteAccess, qc, showProLimit, showReadOnly, usage],
  );

  const handlePublishLimitError = useCallback(
    (err: unknown): boolean => handlePlanAccessError(err),
    [handlePlanAccessError],
  );

  return { guardPublish, handlePublishLimitError, proLimitDialog, usage };
}
